// My Calls — the sourcing member's own call history, straight from CallLogs
// filtered by their userId. Styled to match the Call Logs screen.
// Populated on the live environment (webhook feeds CallLogs there).
import { useCallback, useEffect, useState } from "react";
import { Box, Typography, Button, CircularProgress, Avatar, Stack, Paper } from "@mui/material";
import { styled } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PhoneMissedIcon from "@mui/icons-material/PhoneMissed";
import PhonePausedIcon from "@mui/icons-material/PhonePaused";
import { supabase } from "../../supabaseClient";
import { C } from "./cpSourcingTheme";

const PAGE_SIZE = 15;

const STATUS_META = {
  answered:       { label: "Answered",     color: C.green, bg: C.greenSoft, border: C.greenBd, Icon: CheckCircleIcon },
  missed:         { label: "Missed",       color: C.red,   bg: C.redSoft,   border: C.redBd,   Icon: PhoneMissedIcon },
  "not answered": { label: "Not Answered", color: C.amber, bg: C.amberSoft, border: C.amberBd, Icon: PhonePausedIcon },
};

const CallCard = styled(Paper)(({ sc }) => ({
  background: C.white, border: `1px solid ${C.border}`, borderLeft: `4px solid ${sc}`,
  borderRadius: 10, boxShadow: "0 1px 2px rgba(0,0,0,.04)", overflow: "hidden",
  transition: "box-shadow .15s", "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,.08)" },
}));

// CP / FOS contact-type chip (calllogs.contactType) — CP/FOS only
const CONTACT_TYPE_META = {
  cp: { label: "CP", color: C.indigo, soft: C.indigoSoft, bd: C.indigoBd },
  fos: { label: "FOS", color: C.amber, soft: C.amberSoft, bd: C.amberBd },
};
const ContactTypeChip = ({ type }) => {
  const meta = CONTACT_TYPE_META[String(type || "").trim().toLowerCase()];
  if (!meta) return null;
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", px: "8px", py: "1px", borderRadius: 20,
      background: meta.soft, border: `1px solid ${meta.bd}`, color: meta.color,
      fontSize: 10.5, fontWeight: 700, fontFamily: C.sans, whiteSpace: "nowrap", flexShrink: 0, lineHeight: 1.6,
    }}>
      {meta.label}
    </Box>
  );
};

const fmtDateTime = (ms) => {
  if (!ms) return "—";
  const d = new Date(Number(ms));
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString(undefined, {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
};

const fmtDuration = (sec) => {
  const s = Number(sec) || 0;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
};

export default function MyCallsView({ userId }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCalls = useCallback(async (offset = 0, append = false) => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: err, count } = await supabase
        .from("CallLogs")
        .select(
          "id, customerName, customerNumber, callStatus, contactType, direction, durationSec, startedAt, recordingUrl, created_at",
          { count: "exact" }
        )
        .eq("userId", userId)
        .order("created_at", { ascending: false })
        .range(offset, offset + PAGE_SIZE - 1);
      if (err) throw err;
      setTotal(count || 0);
      setRows((prev) => (append ? [...prev, ...(data || [])] : data || []));
    } catch (e) {
      setError(e?.message || "Failed to load calls.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchCalls(0); }, [fetchCalls]);

  return (
    <Box>
      {error && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography sx={{ color: C.red, mb: 1.5, fontSize: 13, fontFamily: C.sans }}>{error}</Typography>
          <Button variant="outlined" size="small" onClick={() => fetchCalls(0)}
            sx={{ textTransform: "none", fontFamily: C.sans, borderColor: C.indigoBd, color: C.indigo }}>
            Retry
          </Button>
        </Box>
      )}

      {!error && rows.length === 0 && !loading && (
        <Box sx={{
          textAlign: "center", py: 6, px: 2, background: C.white,
          border: `1px solid ${C.border}`, borderRadius: "12px",
        }}>
          <Typography sx={{ color: C.txt3, fontSize: 13.5, fontFamily: C.sans }}>
            No calls yet — your calls will appear here after you start calling your CPs.
          </Typography>
        </Box>
      )}

      <Stack spacing={1.2}>
        {rows.map((log) => {
          const meta = STATUS_META[String(log.callStatus || "").toLowerCase()] ||
            { label: log.callStatus || "—", color: C.slate, bg: C.slateSoft, border: C.slateBd, Icon: PhonePausedIcon };
          const { Icon } = meta;
          return (
            <CallCard key={log.id} sc={meta.color} elevation={0}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: { xs: 1.5, sm: 2 }, flexWrap: "wrap" }}>
                <Avatar sx={{
                  width: 38, height: 38, fontSize: 15, fontWeight: 700,
                  bgcolor: meta.bg, color: meta.color, fontFamily: C.sans,
                }}>
                  {(log.customerName || "?").charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 180 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: C.txt1, fontFamily: C.sans }} noWrap>
                      {log.customerName || log.customerNumber || "Unknown"}
                    </Typography>
                    <ContactTypeChip type={log.contactType} />
                  </Box>
                  <Typography sx={{ color: C.txt3, fontSize: 11.5, fontFamily: C.sans }} noWrap>
                    <span style={{ fontFamily: C.mono }}>{log.customerNumber || "—"}</span>
                    {" · "}{fmtDateTime(log.startedAt || log.created_at)}
                    {" · "}{fmtDuration(log.durationSec)}
                    {log.direction ? ` · ${log.direction}` : ""}
                  </Typography>
                </Box>
                <Box sx={{
                  display: "inline-flex", alignItems: "center", gap: "4px",
                  px: "10px", py: "3px", borderRadius: 20,
                  background: meta.bg, border: `1px solid ${meta.border}`,
                  color: meta.color, fontSize: 12, fontWeight: 700,
                  fontFamily: C.sans, whiteSpace: "nowrap", flexShrink: 0,
                }}>
                  <Icon sx={{ fontSize: 13 }} />
                  {meta.label}
                </Box>
                {log.recordingUrl && (
                  <audio controls preload="none" src={log.recordingUrl} style={{ height: 34, maxWidth: 250 }} />
                )}
              </Box>
            </CallCard>
          );
        })}
      </Stack>

      {loading && <Box sx={{ textAlign: "center", py: 3 }}><CircularProgress size={26} sx={{ color: C.indigo }} /></Box>}

      {!loading && rows.length < total && (
        <Box sx={{ textAlign: "center", py: 2 }}>
          <Button variant="outlined" size="small" onClick={() => fetchCalls(rows.length, true)}
            sx={{ textTransform: "none", fontFamily: C.sans, fontWeight: 600, borderColor: C.indigoBd, color: C.indigo, borderRadius: 2 }}>
            Load more ({rows.length}/{total})
          </Button>
        </Box>
      )}
    </Box>
  );
}
